import { useForm } from '@nerdgeschoss/react-use-form-library';
import { omit, sortBy, uniqBy } from 'lodash';
import React, { useContext } from 'react';

import { ActivitiesContext } from '../../../../components/Activities/Activities';
import { FormForm } from '../../../../components/Form/FormForm';
import { FormSelect } from '../../../../components/Form/Select/FormSelect';
import { Switcher } from '../../../../components/Form/Switcher/Switcher';
import { FormTextField } from '../../../../components/Form/TextField/FormTextField';
import { useModal } from '../../../../components/Modal/PresentedModal';
import { Stack } from '../../../../components/Stack/Stack';
import { graphql } from '../../../../generated';
import { CustomSenderAddress, Shop } from '../../../../generated/graphql';
import { t } from '../../../../locales/translate';
import { getCountries } from '../../../../model/country';
import { useRemoteData } from '../../../../model/data';
import { useMutationHandler } from '../../../../model/graphql';

const SAVE_CUSTOM_ADDRESS = graphql(`
  mutation SaveCustomAddress($id: ID!, $customSenderAddress: SenderAddress!, $useCustomSenderAddress: Boolean!) {
    updateCustomSenderAddress(
      input: { id: $id, customSenderAddress: $customSenderAddress, useCustomSenderAddress: $useCustomSenderAddress }
    ) {
      __typename
      shop {
        useCustomSenderAddress
        customSenderAddress {
          name1
          name2
          name3
          address
          address2
          address3
          postCode
          contact
          countryCode
          phone
          email
          city
        }
      }
    }
  }
`);

export function CustomSenderAddressForm({
  customSenderAddress,
  useCustomSenderAddress,
  onSave,
  shopId,
}: {
  customSenderAddress: CustomSenderAddress;
  useCustomSenderAddress: Shop['useCustomSenderAddress'];
  onSave: () => void;
  shopId: string;
}): JSX.Element {
  const { Modal, open, close } = useModal();
  const [saveCustomAddress] = useMutationHandler(SAVE_CUSTOM_ADDRESS);
  const { reloadActivities } = useContext(ActivitiesContext);

  const [countries] = useRemoteData(getCountries, (db) => {
    return uniqBy(db.table('countries').all, 'countryCode');
  });

  const { fields, valid, dirty, onSubmit, submissionStatus, model } = useForm({
    model: {
      customSenderAddress: customSenderAddress,
      useCustomSenderAddress: useCustomSenderAddress,
    },
    validations: {
      useCustomSenderAddress: 'required',
      customSenderAddress: {
        name1: 'required',
        address: 'required',
        postCode: 'required',
        city: 'required',
        countryCode: 'required',
        email: ['email', 'required'],
      },
    },
    onSubmit: async ({ model }) => {
      const customAddress = model.customSenderAddress;

      if (
        !customAddress.name1 ||
        !customAddress.address ||
        !customAddress.postCode ||
        !customAddress.countryCode ||
        !customAddress.city
      ) {
        return;
      }
      await saveCustomAddress({
        variables: {
          id: shopId,
          customSenderAddress: {
            name1: customAddress.name1,
            name2: customAddress.name2,
            name3: customAddress.name3,
            address: customAddress.address,
            address2: customAddress.address2,
            address3: customAddress.address3,
            postCode: customAddress.postCode,
            contact: customAddress.contact,
            countryCode: customAddress.countryCode,
            phone: customAddress.phone,
            email: customAddress.email,
            city: customAddress.city,
          },
          useCustomSenderAddress: !!model.useCustomSenderAddress,
        },
      });
      reloadActivities();
      onSave();
    },
  });
  return (
    <Modal open={open} title={t.model.shop.useCustomSenderAddress}>
      <FormForm
        onClose={close}
        onSubmit={onSubmit}
        canSubmit={dirty && valid}
        loading={submissionStatus === 'submitting'}
      >
        <Stack>
          <Switcher {...fields.useCustomSenderAddress} labels={[t.general.yes, t.general.no]} />
          {fields.useCustomSenderAddress.value &&
            Object.keys(omit(model.customSenderAddress, '__typename')).map((fieldName: keyof CustomSenderAddress) => {
              if (fieldName === 'countryCode') {
                const selectedCountry = countries.find(
                  (c) => fields.customSenderAddress.value.countryCode?.toLowerCase() === c.countryCode
                );
                return (
                  <FormSelect
                    {...fields.customSenderAddress.fields[fieldName]}
                    key={fieldName}
                    value={selectedCountry?.countryCode?.toUpperCase()}
                    label={t.model.shop.customSenderAddressFields[fieldName] || ''}
                    options={sortBy(countries, 'name').map((c) => ({
                      label: c.name,
                      value: c.countryCode.toUpperCase(),
                    }))}
                    allowEmpty
                  />
                );
              }

              return (
                <FormTextField
                  maxLength={(fieldName as unknown) === 'countryCode' ? 2 : undefined}
                  key={fieldName}
                  {...fields.customSenderAddress.fields[fieldName]}
                  label={t.model.shop.customSenderAddressFields[fieldName] || ''}
                />
              );
            })}
        </Stack>
      </FormForm>
    </Modal>
  );
}
